import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Search, Filter, FileText, Calendar, Clock, Tag, BookOpen, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { mockSermoes, type Sermao, type SermaoStatus } from '@/mocks'
import { useChurchColors } from '@/hooks/useChurchColors'
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
}

const statusLabels: Record<SermaoStatus, string> = {
  publicado: 'Publicado',
  agendado: 'Agendado',
  rascunho: 'Rascunho',
}

const statusBadgeStyles: Record<SermaoStatus, string> = {
  publicado: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200',
  agendado: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200',
  rascunho: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-200',
}

const filterOptions = [
  { value: 'todos', label: 'Todos os status' },
  { value: 'publicado', label: 'Publicados' },
  { value: 'agendado', label: 'Agendados' },
  { value: 'rascunho', label: 'Rascunhos' },
] as const

export function SermoesPage() {
  const { primaryColor } = useChurchColors()
  const [sermoes] = useState<Sermao[]>(() =>
    [...mockSermoes].sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<(typeof filterOptions)[number]['value']>('todos')

  const stats = useMemo(() => {
    const total = sermoes.length
    const publicados = sermoes.filter((sermao) => sermao.status === 'publicado').length
    const agendados = sermoes.filter((sermao) => sermao.status === 'agendado').length
    const rascunhos = sermoes.filter((sermao) => sermao.status === 'rascunho').length

    return { total, publicados, agendados, rascunhos }
  }, [sermoes])

  const filteredSermoes = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    return sermoes.filter((sermao) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [
          sermao.titulo,
          sermao.tema,
          sermao.serie,
          sermao.referencia_principal,
          sermao.descricao,
          sermao.tags.join(' '),
        ]
          .filter(Boolean)
          .some((field) => field?.toLowerCase().includes(normalizedSearch))

      const matchesStatus =
        statusFilter === 'todos' || sermao.status === (statusFilter as SermaoStatus)

      return matchesSearch && matchesStatus
    })
  }, [sermoes, searchTerm, statusFilter])

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Sermoes & Palavra</h1>
          <p className="text-muted-foreground">
            Organize suas mensagens, series e referencias de forma centralizada.
          </p>
        </div>
        <Button 
          asChild 
          size="lg" 
          className="shadow-md"
          style={{ 
            backgroundColor: primaryColor,
            borderColor: primaryColor,
            boxShadow: `0 4px 6px -1px ${primaryColor}33, 0 2px 4px -2px ${primaryColor}33`
          }}
        >
          <Link to="/sermoes/novo" className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            Novo sermao
          </Link>
        </Button>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="grid gap-4 lg:grid-cols-[2fr_1fr]"
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por titulo, tema ou referencia..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="pl-10"
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Status
          </Label>
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as 'rascunho' | 'publicado' | 'agendado' | 'todos')
            }
          >
            <SelectTrigger className="w-full">
              <div className="flex items-center gap-2 text-sm">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Filtrar por status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        <motion.div variants={item}>
          <Card 
            className="border shadow-sm"
            style={{ 
              borderColor: `${primaryColor}33`,
              backgroundColor: `${primaryColor}0d`
            }}
          >
            <CardHeader className="pb-2">
              <CardDescription>Total de sermoes</CardDescription>
              <CardTitle className="text-3xl font-bold">{stats.total}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Mensagens registradas no acervo.</p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={item}>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Publicados</CardDescription>
              <CardTitle className="flex items-center gap-2 text-3xl font-bold">
                {stats.publicados}
                <Badge className={cn('text-xs', statusBadgeStyles.publicado)}>
                  {statusLabels.publicado}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Disponiveis para compartilhar com a igreja.
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={item}>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Agendados</CardDescription>
              <CardTitle className="flex items-center gap-2 text-3xl font-bold">
                {stats.agendados}
                <Badge className={cn('text-xs', statusBadgeStyles.agendado)}>
                  {statusLabels.agendado}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Mensagens em preparacao para os proximos cultos.
              </p>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div variants={item}>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Rascunhos</CardDescription>
              <CardTitle className="flex items-center gap-2 text-3xl font-bold">
                {stats.rascunhos}
                <Badge className={cn('text-xs', statusBadgeStyles.rascunho)}>
                  {statusLabels.rascunho}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Ideias que ainda precisam de revisao ou conclusao.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      {/* Sermoes list */}
      <motion.div variants={container} initial="hidden" animate="show" className="grid gap-6">
        {filteredSermoes.map((sermao) => (
          <motion.div key={sermao.id} variants={item}>
            <Card className="relative overflow-hidden border shadow-sm transition-all hover:shadow-lg">
              <div 
                className="absolute inset-x-0 top-0 h-1"
                style={{ 
                  background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}99, ${primaryColor}4d)`
                }}
              />
              <CardHeader className="pb-4">
                <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <FileText 
                        className="h-5 w-5" 
                        style={{ color: primaryColor }}
                      />
                      {sermao.titulo}
                    </CardTitle>
                    <CardDescription>{sermao.descricao}</CardDescription>
                  </div>
                  <Badge
                    className={cn(
                      'px-3 py-1 text-xs font-semibold',
                      statusBadgeStyles[sermao.status]
                    )}
                  >
                    {statusLabels[sermao.status]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <BookOpen 
                      className="h-4 w-4" 
                      style={{ color: primaryColor }}
                    />
                    <span>{sermao.tema}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {new Date(sermao.data).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{sermao.duracao_estimada} minutos</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm text-muted-foreground">
                    Serie:{' '}
                    <span className="font-medium text-foreground">
                      {sermao.serie || 'Sem serie'}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Referencia:{' '}
                    <span className="font-medium text-foreground">
                      {sermao.referencia_principal}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Tag 
                    className="h-4 w-4" 
                    style={{ color: primaryColor }}
                  />
                  {sermao.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="default">
                    Editar
                  </Button>
                  <Button size="sm" variant="outline">
                    Ver detalhes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {filteredSermoes.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-lg border bg-card p-12 text-center"
        >
          <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-lg font-semibold">Nenhum sermao encontrado</h3>
          <p className="text-sm text-muted-foreground">
            Ajuste os filtros ou cadastre uma nova mensagem.
          </p>
        </motion.div>
      )}
    </div>
  )
}
